// Tạo file mới: src/components/NotificationScreen.js

import React, { useState, useEffect } from 'react';
import { 
  ChevronLeft, 
  Bell, 
  Settings, 
  Check, 
  AlertCircle, 
  Trash2,
  RefreshCw,
  Filter,
  X,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';
import { useFirebaseMessaging } from '../hooks/useFirebaseMessaging';
import { notificationService } from '../services/notificationService';
import { authService } from '../services/apiService';

const NotificationScreen = ({ userData, onBack }) => {
  const [notificationHistory, setNotificationHistory] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
  const [showSettings, setShowSettings] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const {
    messagingToken,
    permission,
    isLoading,
    error,
    requestPermission,
    testNotification,
    lastMessage
  } = useFirebaseMessaging(userData);
  console.log('Firebase Messaging Token:', userData);
  // Handle new messages
  useEffect(() => {
    if (lastMessage) {
      const newNotification = {
        id: Date.now(),
        title: lastMessage.notification?.title || 'Thông báo mới',
        body: lastMessage.notification?.body || '',
        data: lastMessage.data || {},
        timestamp: new Date(),
        read: false,
        type: lastMessage.data?.type || 'general'
      };

      setNotificationHistory(prev => [newNotification, ...prev.slice(0, 99)]); // Keep only 100 recent
      setUnreadCount(prev => prev + 1);

      // Handle specific notification types
      handleNotificationAction(newNotification);
    }
  }, [lastMessage]);

  // Subscribe to notification service events
  useEffect(() => {
    const unsubscribeShipment = notificationService.subscribe('shipment_assigned', (data) => {
      addNotification('Chuyến xe mới', `Bạn có chuyến xe mới #${data.shipmentId}`, 'shipment');
    });

    const unsubscribeStatus = notificationService.subscribe('shipment_status_changed', (data) => {
      addNotification('Cập nhật trạng thái', `Chuyến xe #${data.shipmentId} đã được cập nhật`, 'status');
    });

    const unsubscribeUrgent = notificationService.subscribe('urgent_message', (data) => {
      addNotification('Thông báo khẩn', data.message, 'urgent');
    });

    return () => {
      unsubscribeShipment();
      unsubscribeStatus();
      unsubscribeUrgent();
    };
  }, []);

  const addNotification = (title, body, type = 'general') => {
    const newNotification = {
      id: Date.now(),
      title,
      body,
      timestamp: new Date(),
      read: false,
      type
    };

    setNotificationHistory(prev => [newNotification, ...prev]);
    setUnreadCount(prev => prev + 1);
  };

  const handleNotificationAction = (notification) => {
    const { data } = notification;
    
    switch (data.type) {
      case 'shipment_assigned':
        // Could navigate to shipment detail
        break;
      case 'urgent_message':
        // Show urgent notification
        break;
      default:
        break;
    }
  };

  const markAsRead = (notificationId) => {
    setNotificationHistory(prev => 
      prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotificationHistory(prev => 
      prev.map(n => ({ ...n, read: true }))
    );
    setUnreadCount(0);
  };

  const deleteNotification = (notificationId) => {
    setNotificationHistory(prev => {
      const notification = prev.find(n => n.id === notificationId);
      const newHistory = prev.filter(n => n.id !== notificationId);
      
      // Decrease unread count if deleting unread notification
      if (notification && !notification.read) {
        setUnreadCount(prevCount => Math.max(0, prevCount - 1));
      }
      
      return newHistory;
    });
  };

  const clearAllNotifications = () => {
    setNotificationHistory([]);
    setUnreadCount(0);
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = now - time;
    
    if (diff < 60000) return 'Vừa xong';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} phút trước`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} giờ trước`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)} ngày trước`;
    return time.toLocaleDateString('vi-VN');
  };

  const getPermissionText = () => {
    switch (permission) {
      case 'granted': return 'Đã bật';
      case 'denied': return 'Bị từ chối';
      default: return 'Chưa thiết lập';
    }
  };

  const getPermissionColor = () => {
    switch (permission) {
      case 'granted': return 'text-green-600';
      case 'denied': return 'text-red-600';
      default: return 'text-yellow-600';
    }
  };
  
  const handlePasswordChange = async () => {
    // Reset messages
    setPasswordError('');
    setPasswordSuccess('');
    
    // Validate passwords
    
    
    if (password !== confirmPassword) {
      setPasswordError('Mật khẩu xác nhận không khớp');
      return;
    }
    
    // Change password
    setIsChangingPassword(true);
    try {
      const response = await authService.changePassword(userData.UserName, password);
      
      if (response.success) {
        setPasswordSuccess('Đổi mật khẩu thành công');
        setPassword('');
        setConfirmPassword('');
        // Hide password form after 2 seconds
        setTimeout(() => {
          setShowPasswordForm(false);
          setPasswordSuccess('');
        }, 2000);
      } else {
        setPasswordError(response.message || 'Đổi mật khẩu thất bại');
      }
    } catch (error) {
      setPasswordError('Đã xảy ra lỗi: ' + (error.message || 'Không thể kết nối đến máy chủ'));
    } finally {
      setIsChangingPassword(false);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'shipment':
        return '🚛';
      case 'urgent':
        return '🚨';
      case 'status':
        return '📋';
      default:
        return '📢';
    }
  };

  const filteredNotifications = notificationHistory.filter(notification => {
    switch (filter) {
      case 'unread':
        return !notification.read;
      case 'read':
        return notification.read;
      default:
        return true;
    }
  });

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm sticky top-0 z-10">
        <div className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button 
                onClick={onBack} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Thông báo</h1>
                {unreadCount > 0 && (
                  <p className="text-sm text-blue-600">{unreadCount} thông báo chưa đọc</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Filter Button */}
              <button
                onClick={() => setShowSettings(!showSettings)}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex mt-4 border-b">
            {[
              { key: 'all', label: 'Tất cả', count: notificationHistory.length },
              { key: 'unread', label: 'Chưa đọc', count: unreadCount },
              { key: 'read', label: 'Đã đọc', count: notificationHistory.length - unreadCount }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`flex-1 py-2 px-4 text-sm font-medium border-b-2 transition-colors ${
                  filter === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label} {tab.count > 0 && <span className="ml-1">({tab.count})</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Settings Panel */}
      {showSettings && (
        <div className="bg-white border-b shadow-sm">
          <div className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Cài đặt thông báo</h3>
              <button
                onClick={() => setShowSettings(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Permission Status */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">Trạng thái:</span>
                <span className={`text-sm font-medium ${getPermissionColor()}`}>
                  {getPermissionText()}
                </span>
              </div>
              
              {permission !== 'granted' && (
                <button
                  onClick={requestPermission}
                  disabled={isLoading}
                  className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors text-sm"
                >
                  {isLoading ? 'Đang xử lý...' : 'Bật thông báo'}
                </button>
              )}

              {permission === 'granted' && (
                <div className="flex gap-2">
                  <button
                    onClick={testNotification}
                    className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors text-sm"
                  >
                    Thử nghiệm
                  </button>
                  {unreadCount > 0 && (
                    <button
                      onClick={markAllAsRead}
                      className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors text-sm"
                    >
                      Đọc tất cả
                    </button>
                  )}
                </div>
              )}

              {error && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  {error}
                </div>
              )}
            </div>

            {/* Password Change */}
            <div className="mt-4 border-t pt-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-900">Bảo mật tài khoản</h3>
                <button
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  {showPasswordForm ? 'Đóng' : 'Đổi mật khẩu'}
                </button>
              </div>
              
              {showPasswordForm && (
                <div className="mt-3 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mật khẩu mới
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        placeholder="Nhập mật khẩu mới"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5 text-gray-400" />
                        ) : (
                          <Eye className="h-5 w-5 text-gray-400" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Xác nhận mật khẩu
                    </label>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                      placeholder="Nhập lại mật khẩu mới"
                    />
                  </div>
                  
                  {passwordError && (
                    <div className="p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4 inline mr-1" />
                      {passwordError}
                    </div>
                  )}
                  
                  {passwordSuccess && (
                    <div className="p-2 bg-green-50 border border-green-200 rounded text-green-600 text-sm">
                      <Check className="w-4 h-4 inline mr-1" />
                      {passwordSuccess}
                    </div>
                  )}
                  
                  <button
                    onClick={handlePasswordChange}
                    disabled={isChangingPassword}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition-colors text-sm flex items-center justify-center gap-1"
                  >
                    {isChangingPassword ? (
                      <>Đang xử lý...</>
                    ) : (
                      <>
                        <Lock className="w-4 h-4" />
                        Đổi mật khẩu
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
            
            {/* Actions */}
            {notificationHistory.length > 0 && (
              <div className="flex gap-2 mt-4">
                <button
                  onClick={clearAllNotifications}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors text-sm flex items-center justify-center gap-1"
                >
                  <Trash2 className="w-4 h-4" />
                  Xóa tất cả
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Notifications List */}
      <div className="p-4 pb-20">
        {filteredNotifications.length > 0 ? (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`bg-white rounded-lg shadow-sm border overflow-hidden ${
                  !notification.read ? 'border-l-4 border-blue-500 bg-blue-50' : 'border-gray-200'
                }`}
              >
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="text-2xl">
                        {getNotificationIcon(notification.type)}
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900 truncate">
                            {notification.title}
                          </h4>
                          {!notification.read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full ml-2 flex-shrink-0"></div>
                          )}
                        </div>
                        
                        <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                          {notification.body}
                        </p>
                        
                        <div className="flex items-center justify-between mt-3">
                          <p className="text-xs text-gray-400">
                            {formatTime(notification.timestamp)}
                          </p>
                          
                          <div className="flex items-center gap-1">
                            {!notification.read && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                              >
                                Đánh dấu đã đọc
                              </button>
                            )}
                            
                            <button
                              onClick={() => deleteNotification(notification.id)}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title="Xóa thông báo"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-lg p-12 text-center shadow-sm">
            <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'unread' ? 'Không có thông báo chưa đọc' : 
               filter === 'read' ? 'Không có thông báo đã đọc' : 
               'Chưa có thông báo nào'}
            </h3>
            <p className="text-gray-500">
              {filter === 'all' 
                ? 'Thông báo sẽ xuất hiện ở đây khi có cập nhật mới'
                : `Chuyển sang tab khác để xem thông báo`
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationScreen;
